import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

import { lookupManualOrderContracts } from '../components/ManualOrderEntry/contractLookup';
import type { ContractSelection } from '../components/ManualOrderEntry/contractSelection';
import { tradingApi } from '../services/api';

interface UseManualOrderContractLookupOptions {
  symbol: string;
  secType: string;
  setError: Dispatch<SetStateAction<string | null>>;
  setShowDropdown: Dispatch<SetStateAction<boolean>>;
}

/** Own contract-search results and the asynchronous lookup workflow. */
export const useManualOrderContractLookup = ({
  symbol,
  secType,
  setError,
  setShowDropdown,
}: UseManualOrderContractLookupOptions) => {
  const [lookupResults, setLookupResults] = useState<ContractSelection[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  const handleLookup = useCallback(async () => {
    if (!symbol.trim()) {
      setError('Enter a symbol before lookup.');
      return;
    }
    setLookupLoading(true);
    setError(null);
    setLookupResults([]);
    setShowDropdown(true);
    try {
      const result = await lookupManualOrderContracts(
        symbol,
        secType,
        (request) => tradingApi.lookupOrderContracts(request),
      );
      setLookupResults(result.contracts);
      if (result.error) setError(result.error);
    } finally {
      setLookupLoading(false);
    }
  }, [secType, setError, setShowDropdown, symbol]);

  return { lookupResults, setLookupResults, lookupLoading, handleLookup };
};
